import { useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "react-router-dom"
import { CheckCircle2 } from "lucide-react"
import { StepHeader } from "@/components/activation/StepHeader"
import { AskRobPanel } from "@/components/activation/AskRobPanel"
import { StepNavigation } from "@/components/activation/StepNavigation"
import { StrategyRecommendation } from "@/components/activation/StrategyRecommendation"
import { SelectableCard, CheckIndicator } from "@/components/activation/SelectableCard"
import { strategySchema } from "@/components/activation/schemas/strategy"
import {
  INVESTMENT_STRATEGIES,
  OPERATING_STYLES,
  normalizeOperatingPreference,
  recommendStrategies,
} from "@/components/activation/data/strategies"
import { useAskRobChat } from "@/hooks/use-ask-rob-chat"
import { useActivation } from "@/lib/activation-context"

const PROMPTS = [
  "Help Me Select My Strategy",
]

const STAT_LABELS = [
  { key: "cashflow", label: "Cashflow" },
  { key: "equity", label: "Equity Growth" },
  { key: "complexity", label: "Complexity" },
  { key: "speed", label: "Return Speed" },
  { key: "builderHeavy", label: "Builder Heavy" },
]

export function StrategyStep() {
  const { step1, step2, saveStep, askStepTwoAgent, serverError } = useActivation()
  const navigate = useNavigate()
  const [showStrategyExplorer, setShowStrategyExplorer] = useState(false)
  const form = useForm({
    resolver: zodResolver(strategySchema),
    defaultValues: {
      operatingPreference: normalizeOperatingPreference(step2.operatingPreference),
      primaryStrategy: step2.primaryStrategy || "",
      secondaryStrategy: step2.secondaryStrategy,
    },
    mode: "onChange",
  })
  const askRobChat = useAskRobChat()

  const operatingPreference = useWatch({ control: form.control, name: "operatingPreference" })
  const primary = useWatch({ control: form.control, name: "primaryStrategy" })
  const secondary = useWatch({ control: form.control, name: "secondaryStrategy" })
  const submitError = form.formState.errors.root?.message || serverError
  const recommendation = primary
    ? recommendStrategies({
      operatingPreference,
      stepOneValues: step1,
      primaryStrategy: primary,
      secondaryStrategy: secondary,
    })
    : null
  const secondaryStrategies = INVESTMENT_STRATEGIES.filter((strategyItem) => strategyItem.id !== primary)

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await saveStep(2, values)
      navigate("/activation/step/3")
    } catch (error) {
      form.setError("root", { message: error.message })
    }
  })

  const updateOperatingPreference = (id) => {
    form.setValue("operatingPreference", id, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
    form.setValue("primaryStrategy", "", {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
    form.setValue("secondaryStrategy", null, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
    setShowStrategyExplorer(false)
  }

  const selectOperatingPreference = (id) => {
    updateOperatingPreference(operatingPreference === id ? "" : id)
  }

  const selectPrimary = (id) => {
    form.setValue("primaryStrategy", id, { shouldValidate: true, shouldTouch: true })
    if (secondary === id) form.setValue("secondaryStrategy", null, { shouldValidate: true })
  }

  const toggleSecondary = (id) => {
    form.setValue("secondaryStrategy", secondary === id ? null : id, { shouldValidate: true, shouldTouch: true })
  }

  const applySuggestion = (messageIndex, suggestionId) => {
    const suggestion = askRobChat.findSuggestion(messageIndex, suggestionId)
    if (!suggestion || suggestion.status === "applied" || suggestion.status === "dismissed") return

    if (suggestion.field === "operatingPreference") {
      updateOperatingPreference(normalizeOperatingPreference(suggestion.suggestedValue))
      askRobChat.updateSuggestionStatus(messageIndex, suggestionId, "applied")
      return
    }

    if (suggestion.field === "primaryStrategy") {
      form.setValue("primaryStrategy", suggestion.suggestedValue ?? "", {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      })
      if (form.getValues("secondaryStrategy") === suggestion.suggestedValue) {
        form.setValue("secondaryStrategy", null, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        })
      }
      askRobChat.updateSuggestionStatus(messageIndex, suggestionId, "applied")
      return
    }

    if (suggestion.field === "secondaryStrategy") {
      if (form.getValues("primaryStrategy") === suggestion.suggestedValue) {
        askRobChat.updateSuggestionStatus(messageIndex, suggestionId, "dismissed")
        return
      }

      form.setValue("secondaryStrategy", suggestion.suggestedValue ?? null, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      })
      askRobChat.updateSuggestionStatus(messageIndex, suggestionId, "applied")
    }
  }

  const dismissSuggestion = askRobChat.dismissSuggestion

  const askRob = async (message) => {
    await askRobChat.submitMessage(message, ({ message: userMessage, conversation }) => (
      askStepTwoAgent({
        message: userMessage,
        conversation,
        currentValues: form.getValues(),
        stepOneValues: step1,
      })
    ))
  }

  return (
    <form onSubmit={onSubmit} noValidate className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="mx-auto w-full max-w-7xl px-6 py-12 sm:px-8 lg:px-12">
        <StepHeader
          step={2}
          title="Help Me Select My Strategy"
          description="Align your strategy with locked targets, capital, and operating capacity."
        />

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-5">
          <div className="order-2 space-y-8 lg:order-1 lg:col-span-3">
            <div className="space-y-4">
              <div>
                <h2 className="mb-1 text-sm font-semibold text-foreground">
                  Operating Style <span className="text-destructive">*</span>
                </h2>
                <p className="text-xs text-muted-foreground">
                  Delivery involvement and ownership model
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {OPERATING_STYLES.map((preference) => {
                  const selected = operatingPreference === preference.id
                  return (
                    <SelectableCard
                      key={preference.id}
                      selected={selected}
                      onClick={() => selectOperatingPreference(preference.id)}
                      padding="p-3"
                      variant="secondary"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h3 className="mb-1 text-sm font-bold text-foreground">{preference.title}</h3>
                          <p className="text-xs text-muted-foreground">{preference.description}</p>
                          <p className="mt-2 text-[11px] font-medium text-muted-foreground">
                            Examples: {preference.examples}
                          </p>
                        </div>
                        <CheckIndicator selected={selected} />
                      </div>
                    </SelectableCard>
                  )
                })}
              </div>
            </div>

            {operatingPreference && !primary ? (
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-sm font-semibold text-foreground">
                  Rob will challenge the fit before recommending a strategy.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Targets, capital, timeframe and involvement will be weighed together first.
                </p>
              </div>
            ) : null}

            <StrategyRecommendation
              recommendation={recommendation}
              onExplore={() => setShowStrategyExplorer(true)}
            />

            {showStrategyExplorer ? (
              <div className="space-y-4">
                <div>
                  <h2 className="mb-1 text-sm font-semibold text-foreground">
                    Primary Strategy <span className="text-destructive">*</span>
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Core acquisition focus
                  </p>
                </div>

                <div className="space-y-3">
                  {INVESTMENT_STRATEGIES.map((strategyItem) => {
                    const selected = primary === strategyItem.id
                    return (
                      <SelectableCard
                        key={strategyItem.id}
                        selected={selected}
                        onClick={() => selectPrimary(strategyItem.id)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="mb-1 text-sm font-bold text-foreground">{strategyItem.title}</h3>
                            <p className="text-xs text-muted-foreground">{strategyItem.subtitle}</p>
                          </div>
                          <CheckIndicator selected={selected} />
                        </div>

                        {selected ? (
                          <div className="mt-4 space-y-4 border-t border-border pt-4">
                            <div className="space-y-2">
                              {strategyItem.details.map((detail) => (
                                <div key={detail} className="flex items-start gap-2">
                                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                                  <p className="text-xs text-foreground">{detail}</p>
                                </div>
                              ))}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              {STAT_LABELS.map(({ key, label }) => (
                                <div key={key} className="rounded-lg border border-border bg-muted/50 p-3">
                                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                                    {label}
                                  </p>
                                  <p className="text-sm font-bold text-foreground">{strategyItem.stats[key]}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </SelectableCard>
                    )
                  })}
                </div>
              </div>
            ) : null}

            {showStrategyExplorer && primary ? (
              <div className="space-y-4">
                <div>
                  <h2 className="mb-1 text-sm font-semibold text-foreground">Optional: Secondary Strategy</h2>
                  <p className="text-xs text-muted-foreground">
                    Supporting strategy for opportunistic deal flow
                  </p>
                </div>

                <div className="space-y-3">
                  {secondaryStrategies.map((strategyItem) => {
                    const selected = secondary === strategyItem.id
                    return (
                      <SelectableCard
                        key={strategyItem.id}
                        selected={selected}
                        onClick={() => toggleSecondary(strategyItem.id)}
                        disabled={!primary}
                        variant="secondary"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="mb-1 text-sm font-bold text-foreground">{strategyItem.title}</h3>
                            <p className="text-xs text-muted-foreground">{strategyItem.subtitle}</p>
                          </div>
                          <CheckIndicator selected={selected} />
                        </div>
                      </SelectableCard>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </div>

          <div className="order-1 lg:order-2 lg:col-span-2">
            <AskRobPanel
              subtitle="Rob will diagnose involvement first, then recommend strategy routes."
              prompts={PROMPTS}
              messages={askRobChat.messages}
              isLoading={askRobChat.isLoading}
              error={askRobChat.error}
              onAsk={askRob}
              onApplySuggestion={applySuggestion}
              onDismissSuggestion={dismissSuggestion}
            />
          </div>
        </div>

        <StepNavigation
          stepNumber={2}
          canContinue={!!operatingPreference && !!primary && form.formState.isValid}
          isSubmitting={form.formState.isSubmitting}
          continueLabel="Lock Strategy"
          onBack={() => navigate("/activation/step/1")}
        />
        {submitError ? <p className="mt-4 text-center text-sm text-destructive">{submitError}</p> : null}
      </div>
    </form>
  )
}
